// ============================================================
// PREZENCE — Presence Globe (WebGL, three.js)
// Faithful port of Claude Design's prezence-orb.js "globe" variant.
// Loads three.js from CDN on mount (cached after first load), renders
// an icosahedron wireframe + presence nodes + orbiting rings + halo,
// with the same smooth rotation. Cleans up on unmount.
// ============================================================
import { useEffect, useRef } from 'react';

const THREE_SRC = 'https://unpkg.com/three@0.160.0/build/three.module.js';
let threeP = null;
const loadThree = () => (threeP ||= import(/* webpackIgnore: true */ THREE_SRC));

export default function PresenceGlobe({ style, className }) {
  const hostRef = useRef(null);

  useEffect(() => {
    let renderer, raf, ro, disposed = false;
    const host = hostRef.current;
    if (!host) return;

    loadThree().then((THREE) => {
      if (disposed || !host) return;
      const w = host.clientWidth || 400, h = host.clientHeight || 300;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h);
      renderer.domElement.style.display = 'block';
      host.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(38, w / h, 0.1, 100);
      camera.position.set(0, 0.6, 7.2);
      camera.lookAt(0, 0, 0);

      const teal = new THREE.Color('#63dcbc');
      const blue = new THREE.Color('#3ba8ff');
      const group = new THREE.Group();
      scene.add(group);

      // Core sphere
      group.add(new THREE.Mesh(
        new THREE.IcosahedronGeometry(2.05, 4),
        new THREE.MeshBasicMaterial({ color: 0x0a2a3a, transparent: true, opacity: 0.55 })
      ));
      // Wireframe
      group.add(new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.IcosahedronGeometry(2.1, 2)),
        new THREE.LineBasicMaterial({ color: blue, transparent: true, opacity: 0.34 })
      ));
      // Halo
      group.add(new THREE.Mesh(
        new THREE.SphereGeometry(2.5, 48, 48),
        new THREE.MeshBasicMaterial({ color: teal, transparent: true, opacity: 0.045, side: THREE.BackSide })
      ));
      // Presence nodes distributed on the sphere (Fibonacci)
      const N = 46, pos = new Float32Array(N * 3), col = new Float32Array(N * 3);
      for (let i = 0; i < N; i++) {
        const y = 1 - (i / (N - 1)) * 2, r = Math.sqrt(Math.max(0, 1 - y * y));
        const t = i * 2.399963;
        const p = new THREE.Vector3(Math.cos(t) * r, y, Math.sin(t) * r).multiplyScalar(2.16);
        pos.set([p.x, p.y, p.z], i * 3);
        const c = i % 5 === 0 ? new THREE.Color('#f59e0b') : (i % 3 ? teal : blue);
        col.set([c.r, c.g, c.b], i * 3);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      group.add(new THREE.Points(g, new THREE.PointsMaterial({ size: 0.085, vertexColors: true, transparent: true, opacity: 0.95 })));
      // Orbiting rings
      for (let k = 0; k < 3; k++) {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(2.75 + k * 0.32, 0.006, 8, 160),
          new THREE.MeshBasicMaterial({ color: k % 2 ? blue : teal, transparent: true, opacity: 0.3 })
        );
        ring.rotation.x = Math.PI / 2 + (k - 1) * 0.42;
        ring.rotation.z = k * 0.5;
        ring.userData.spin = 0.12 + k * 0.07;
        group.add(ring);
      }

      const clock = { t: 0 };
      const tick = () => {
        raf = requestAnimationFrame(tick);
        clock.t += 0.0125;
        group.rotation.y += 0.0034;
        group.rotation.x = Math.sin(clock.t * 0.5) * 0.11;
        group.children.forEach((c) => { if (c.userData.spin) c.rotation.z += c.userData.spin * 0.01; });
        renderer.render(scene, camera);
      };
      tick();

      ro = new ResizeObserver(() => {
        const W = host.clientWidth || w, H = host.clientHeight || h;
        renderer.setSize(W, H); camera.aspect = W / H; camera.updateProjectionMatrix();
      });
      ro.observe(host);
    }).catch(() => { /* CDN blocked — hero simply shows no globe, no crash */ });

    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      if (renderer) { renderer.dispose?.(); renderer.domElement?.remove(); }
    };
  }, []);

  return <div ref={hostRef} className={className} style={style} aria-hidden />;
}
